import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, StatusBadge, ConfirmModal } from '../components/common';
import { EditTopicModal, TopicDetailsModal } from '../components/modals';
import { formatDate } from '../utils/formatters';
import { SORT_BY } from '../constants';

/**
 * All Topics Management page - Admin view of all topics
 */
const AllTopicsManagement = ({ user }) => {
  const [topics, setTopics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(SORT_BY.MEETING);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [editTopic, setEditTopic] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [confirmState, setConfirmState] = useState(null);
  const [statusError, setStatusError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [sortBy, filterStatus]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const loadData = async () => {
    try {
      const [topicsData, usersData] = await Promise.all([
        api.get('/topics'),
        api.get('/users/list')
      ]);
      setTopics(topicsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTopicStatus = async (topicId, status) => {
    try {
      const isClosed = status === 'closed';
      await api.patch(`/topics/${topicId}`, { 
        closed: isClosed,
        closedAt: isClosed ? new Date().toISOString() : null
      });
      loadData();
    } catch (error) {
      console.error('Failed to update topic status:', error);
      setStatusError('Failed to update topic status');
    }
  };

  const handleDeleteTopic = (topicId) => {
    setConfirmState({
      message: 'Are you sure you want to delete this topic? This action cannot be undone.',
      confirmLabel: 'Delete Topic',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/topics/${topicId}`);
          loadData();
        } catch (error) {
          console.error('Failed to delete topic:', error);
        }
      },
    });
  };

  // Filter topics by status
  const filteredTopics = useMemo(() => {
    return topics.filter(topic => {
      if (filterStatus === 'all') return true;
      const isClosed = topic.closedAt || topic.closed;
      if (filterStatus === 'open') return !isClosed;
      if (filterStatus === 'closed') return isClosed;
      return true;
    });
  }, [topics, filterStatus]);

  // Group topics by meeting or submitter
  const groupedTopics = useMemo(() => {
    const grouped = {};
    
    if (sortBy === SORT_BY.MEETING) {
      filteredTopics.forEach(topic => {
        const key = topic.meetingId || 'unassigned';
        const name = topic.meetingName || 'Unassigned';
        if (!grouped[key]) grouped[key] = { name, topics: [] };
        grouped[key].topics.push(topic);
      });
    } else {
      // Sort by submitter
      filteredTopics.forEach(topic => {
        const key = topic.createdBy || 'unknown';
        const name = topic.creatorName || 'Unknown';
        if (!grouped[key]) grouped[key] = { name, topics: [] };
        grouped[key].topics.push(topic);
      });
    }
    
    return grouped;
  }, [filteredTopics, sortBy]);

  // Status counts
  const statusCounts = {
    all: topics.length,
    open: topics.filter(t => !t.closedAt && !t.closed).length,
    closed: topics.filter(t => t.closedAt || t.closed).length,
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">All Topics</h1>
      </div>

      {statusError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{statusError}</div>
      )}

      {/* Sort and Filter buttons */}
      <div className="flex gap-1" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div className="flex gap-1">
          <Button
            variant={sortBy === SORT_BY.MEETING ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSortBy(SORT_BY.MEETING)}
          >
            Sort by Meeting
          </Button>
          <Button
            variant={sortBy === 'submitter' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setSortBy('submitter')}
          >
            Sort by Submitter
          </Button>
          <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 0.25rem' }} />
          <Button
            variant="secondary"
            size="small"
            onClick={() => setCollapsedGroups(new Set(Object.keys(groupedTopics)))}
          >
            Collapse All
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setCollapsedGroups(new Set())}
          >
            Expand All
          </Button>
        </div>

        <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 0.5rem' }}></div>

        <div className="flex gap-1">
          <Button
            variant={filterStatus === 'all' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setFilterStatus('all')}
          >
            All ({statusCounts.all})
          </Button>
          <Button
            variant={filterStatus === 'open' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setFilterStatus('open')}
          >
            Open ({statusCounts.open})
          </Button>
          <Button
            variant={filterStatus === 'closed' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setFilterStatus('closed')}
          >
            Closed ({statusCounts.closed})
          </Button>
        </div>
      </div>

      {/* Results */}
      {filteredTopics.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No topics found"
          message="No topics match your current filter."
        />
      ) : (
        Object.entries(groupedTopics).map(([key, group]) => (
          <div key={key} style={{ marginBottom: '1.5rem' }}>
            <h2
              onClick={() => toggleGroup(key)}
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--color-primary)',
                marginBottom: '0.5rem',
                paddingBottom: '0.25rem',
                borderBottom: '2px solid var(--color-primary)',
                cursor: 'pointer',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              <span style={{ fontSize: '0.75rem' }}>{collapsedGroups.has(key) ? '▶' : '▼'}</span>
              {group.name} ({group.topics.length})
            </h2>
            {!collapsedGroups.has(key) && (
            <table className="data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Topic</th>
                  {sortBy === SORT_BY.MEETING ? <th>Submitted By</th> : <th>Meeting</th>}
                  <th>Date Added</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.topics.map(topic => {
                  const isClosed = topic.closedAt || topic.closed;
                  return (
                    <tr
                      key={topic.id}
                      style={{ opacity: isClosed ? 0.7 : 1, cursor: 'pointer' }}
                      onClick={() => setSelectedTopic(topic)}
                    >
                      <td>{topic.title}</td>
                      {sortBy === SORT_BY.MEETING ? (
                        <td>{topic.creatorName || '-'}</td>
                      ) : (
                        <td>{topic.meetingName || '-'}</td>
                      )}
                      <td>{formatDate(topic.createdAt)}</td>
                      <td>
                        <span className={`badge ${isClosed ? 'badge-closed' : 'badge-open'}`}>
                          {isClosed ? 'Closed' : 'Open'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {user.isAdmin && (
                          <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditTopic(topic);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              style={{ color: 'var(--color-danger)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTopic(topic.id);
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
        ))
      )}

      {/* Modals */}
      {selectedTopic && (
        <TopicDetailsModal
          topicId={selectedTopic.id}
          onClose={() => setSelectedTopic(null)}
        />
      )}

      {editTopic && (
        <EditTopicModal
          topic={editTopic}
          onClose={() => setEditTopic(null)}
          onSave={() => {
            setEditTopic(null);
            loadData();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

AllTopicsManagement.propTypes = {
  user: PropTypes.object.isRequired,
};

export default AllTopicsManagement;
